import type {
  CoupledDimensionGroupResult,
  IntervalJSON,
} from '@highimpact-frontend/api-types';
import { ErrorStatus } from '@highimpact/dashboard-ui';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import { TimeRangeUnionForm } from '@highimpact/time-interval-selector';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MigratedPlotFormProps } from '../../../../forms/formHandlerShared';
import {
  ensureConfiguredOmbrothermic,
  ensureConfiguredWindrose,
  getIntervals,
  isValid,
  setElementCoupledSourcesFromResolvedSources,
  setIntervals,
} from '../../../../state/dashboard-element-operations';
import {
  areCoupledSelectionsEqual,
  buildCoupledSelectionFromElement,
  coupledRowToSources,
  getCoupledWidgetDimensionConfig,
  syncCoupledSelectionFromCatalog,
  type CoupledWidgetKind,
} from '../shared/coupledDimensionSelection';
import {
  PlotConfigFormDivider,
  PlotConfigFormShell,
  PlotConfigInvalidStatus,
  PlotConfigSubmitButton,
} from '../shared/plotConfigFormLayout';
import { resolveSourcesFromCatalog } from '../shared/resolveSourcesFromCatalog';
import { useWidgetSourceSelection } from '../shared/useWidgetSourceSelection';

function ensureConfiguredCoupledPlot(
  widget: CoupledWidgetKind,
  config: MigratedPlotFormProps['config']
) {
  return widget === 'windrose'
    ? ensureConfiguredWindrose(config)
    : ensureConfiguredOmbrothermic(config);
}

export interface CoupledDimensionPlotFormProps extends MigratedPlotFormProps {
  widget: CoupledWidgetKind;
}

function CoupledDimensionPlotForm({
  widget,
  config,
  project_id,
  site_id,
  campaigns,
  onSubmit,
  onChange,
}: CoupledDimensionPlotFormProps) {
  const { groupKey, dimensionOrder } = getCoupledWidgetDimensionConfig(widget);
  const scope = useMemo(() => ({ project_id, site_id }), [project_id, site_id]);
  const { loading, error, data } = useWidgetSourceSelection({ widget, scope });

  const initialCoupledSelection = useMemo(
    () =>
      buildCoupledSelectionFromElement(
        data && data.response_type === 'dimensions' ? data : null,
        config,
        groupKey,
        dimensionOrder
      ),
    [config, data, dimensionOrder, groupKey]
  );

  const [coupledSelection, setCoupledSelection] = useState<
    Record<string, CoupledDimensionGroupResult[]>
  >(initialCoupledSelection);
  const coupledSelectionRef = useRef(coupledSelection);
  coupledSelectionRef.current = coupledSelection;

  const [submitCount, setSubmitCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [validation, setValidation] = useState(isValid(config));

  useEffect(() => {
    setCoupledSelection(initialCoupledSelection);
  }, [initialCoupledSelection]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setCoupledSelection((previousSelection) => {
      const syncedSelection = syncCoupledSelectionFromCatalog(
        data,
        groupKey,
        dimensionOrder,
        previousSelection
      );

      return areCoupledSelectionsEqual(previousSelection, syncedSelection)
        ? previousSelection
        : syncedSelection;
    });
  }, [data, dimensionOrder, groupKey]);

  const applyCoupledSelection = useCallback(
    (nextSelection: Record<string, CoupledDimensionGroupResult[]>) => {
      const configuredElement = ensureConfiguredCoupledPlot(widget, config);
      const selectedRow = nextSelection[groupKey]?.[0];

      if (!selectedRow) {
        const clearedConfig = setElementCoupledSourcesFromResolvedSources(
          [],
          configuredElement,
          dimensionOrder
        );
        setValidation(isValid(clearedConfig));
        onChange(clearedConfig);
        return;
      }

      const flattenedSources = coupledRowToSources(selectedRow, dimensionOrder);
      const resolvedSources = resolveSourcesFromCatalog(
        data,
        flattenedSources.map((source) => ({
          measure_point_id: source.measure_point_id,
          measure_type_id: source.measure_type_id,
        })),
        flattenedSources
      );
      const newConfig = setElementCoupledSourcesFromResolvedSources(
        resolvedSources,
        configuredElement,
        dimensionOrder
      );
      setValidation(isValid(newConfig));
      onChange(newConfig);
    },
    [config, data, dimensionOrder, groupKey, onChange, widget]
  );

  const handleIntervalsUpdate = (intervals: IntervalJSON[]) => {
    const newConfig = setIntervals(
      intervals,
      ensureConfiguredCoupledPlot(widget, config)
    );
    setValidation(isValid(newConfig));
    onChange(newConfig);
  };

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setSubmitCount(submitCount + 1);
    if (validation[0]) {
      setIsSending(true);
      onSubmit(config).finally(() => setIsSending(false));
    }
  };

  const dataSourceErrors = validation?.[1]?.dataSource ?? [];
  const dataSourceHasError: ErrorStatus =
    submitCount > 0
      ? { tag: 'Submited', errors: dataSourceErrors }
      : { tag: 'Not submitted' };

  return (
    <PlotConfigFormShell onSubmit={handleSubmit}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialCoupledSelection={coupledSelection}
        onCoupledGroupSelect={(selectedGroupKey, selectedRows) => {
          const nextSelection = {
            ...coupledSelectionRef.current,
            [selectedGroupKey]: selectedRows,
          };
          setCoupledSelection(nextSelection);
          applyCoupledSelection(nextSelection);
        }}
        coupledGroupsSingleSelect
        matchControlPanelStyle
      />

      <PlotConfigInvalidStatus error={dataSourceHasError} />

      <PlotConfigFormDivider />

      <TimeRangeUnionForm
        onChange={handleIntervalsUpdate}
        campaigns={campaigns}
        noDivision
        defaultIntervals={getIntervals(config)}
        matchControlPanelStyle
      />

      <PlotConfigSubmitButton
        isSending={isSending}
        loading={loading}
        disabled={
          dataSourceHasError.tag === 'Submited' &&
          dataSourceHasError.errors.length > 0
        }
      />
    </PlotConfigFormShell>
  );
}

export default CoupledDimensionPlotForm;
